<template>
    <div class="relative">
        <button
            ref="trigger"
            class="flex w-full rounded border border-slate-200 bg-white px-3 py-4 text-left hover:border-slate-300"
            aria-label="Lists"
            aria-haspopup="true"
            :aria-expanded="dropdownOpen"
            @click.prevent="toggleDropdown"
        >
            <div class="flex">
                <AppIcon
                    :name="selectedSource?.icon"
                    class="my-auto mr-3 h-4 w-4 shrink-0"
                    :class="selectedSource?.iconClass"
                />
                <div class="flex flex-col">
                    <div class="text-sm font-semibold leading-none">{{ selectedSource?.name }}</div>
                    <div class="text-xs leading-none mt-1 text-slate-600">Platform</div>
                </div>
            </div>

            <AppIcon name="chevron-down" class="my-auto ml-auto mr-1 h-3.5 w-3.5 shrink-0" />
        </button>

        <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-out duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="dropdownOpen"
                ref="dropdown"
                class="no-scrollbar absolute left-0 z-50 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-xl"
                :class="dropdownPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'"
                :style="{ maxHeight: `${dropdownMaxHeight}px` }"
            >
                <div>
                    <div
                        v-for="source in visiblePlatforms"
                        :key="source.id"
                        class="text-md group flex cursor-pointer border-b border-slate-200 px-3 py-3.5 hover:bg-slate-50"
                        @click="onSelect('platform', source)"
                    >
                        <div class="flex">
                            <AppIcon
                                :name="source.icon"
                                class="my-auto mr-3 h-4 w-4 shrink-0"
                                :class="source.iconClass"
                            />
                            <div class="flex flex-col">
                                <div class="text-sm font-semibold leading-none">{{ source?.name }}</div>
                                <div class="text-xs text-slate-600 leading-none mt-1">Platform</div>
                            </div>
                        </div>
                        <!-- <div class="my-auto ml-auto flex">
                            <div
                                v-if="source.is_beta"
                                class="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 group-hover:bg-slate-200"
                            >
                                Beta
                            </div>
                        </div> -->
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ref, onMounted, onUnmounted } from 'vue'
import _ from 'lodash'

export default {
    name: 'DropdownDiscoverPlatform',

    props: {
        initialSelectedSourceId: {
            type: String,
            default: null,
        },
        platforms: {
            type: Array,
            required: true,
        },
        counts: {
            type: Object,
        },
    },

    emits: ['change'],

    setup() {
        const dropdownOpen = ref(false)
        const dropdown = ref(null)
        const trigger = ref(null)

        // close on click outside
        const clickHandler = ({ target }) => {
            if (!dropdownOpen.value || dropdown.value?.contains(target) || trigger.value?.contains(target)) return
            dropdownOpen.value = false
        }

        // close if the esc key is pressed
        const keyHandler = ({ keyCode }) => {
            if (!dropdownOpen.value || keyCode !== 27) return
            dropdownOpen.value = false
        }

        onMounted(() => {
            document.addEventListener('click', clickHandler)
            document.addEventListener('keydown', keyHandler)
        })

        onUnmounted(() => {
            document.removeEventListener('click', clickHandler)
            document.removeEventListener('keydown', keyHandler)
        })

        return {
            dropdownOpen,
            dropdown,
            trigger,
        }
    },

    data() {
        return {
            selectedSourceId: null,
            dropdownPlacement: 'bottom',
            dropdownMaxHeight: 384,

            availablePlatforms: {
                instagram: {
                    id: 'instagram',
                    name: 'Instagram',
                    icon: 'instagram',
                    iconClass: 'text-instagram',
                    description: 'You are searching on our database of creators on Instagram.',
                    is_beta: true,
                },
                tiktok: {
                    id: 'tiktok',
                    name: 'TikTok',
                    icon: 'tiktok',
                    iconClass: 'text-tiktok',
                    description: 'You are searching on our database of creators on TikTok.',
                    is_beta: true,
                },
                youtube: {
                    id: 'youtube',
                    name: 'YouTube',
                    icon: 'youtube',
                    iconClass: 'text-youtube',
                    description: 'You are searching on our database of creators on YouTube.',
                    is_beta: false,
                },
                twitch: {
                    id: 'twitch',
                    name: 'Twitch',
                    icon: 'twitch',
                    iconClass: 'text-twitch',
                    description: 'You are searching on our database of creators on Twitch.',
                    is_beta: false,
                },
                twitter: {
                    id: 'twitter',
                    name: 'X',
                    icon: 'x-twitter',
                    iconClass: 'text-x',
                    description: 'You are searching on our database of creators on X.',
                    is_beta: true,
                },
            },
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary']),

        selectedSource() {
            return this.availablePlatforms[this.selectedSourceId]
        },

        visiblePlatforms() {
            return _.reduce(
                this.availablePlatforms,
                (result, availablePlatform, platformId) => {
                    if (this.platforms.includes(platformId)) {
                        result.push({ ...availablePlatform })
                    }

                    return result
                },
                [],
            )
        },
    },

    // created() {
    //     this.selectedSourceId = this.initialSelectedSourceId
    // },

    watch: {
        initialSelectedSourceId: {
            handler(newValue) {
                this.selectedSourceId = newValue
            },
            immediate: true,
        }
    },

    methods: {
        toggleDropdown() {
            if (this.dropdownOpen) {
                this.dropdownOpen = false
                return
            }

            this.updateDropdownPlacement()
            this.dropdownOpen = true
        },

        updateDropdownPlacement() {
            const triggerEl = this.trigger?.$el || this.trigger
            if (!triggerEl) return

            const viewportPadding = 8
            const preferredDropdownHeight = 384
            const minimumDropdownHeight = 120
            const rect = triggerEl.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
            const spaceAbove = rect.top - viewportPadding
            const shouldOpenAbove = spaceBelow < preferredDropdownHeight && spaceAbove > spaceBelow
            const availableHeight = Math.max(
                minimumDropdownHeight,
                Math.min(preferredDropdownHeight, shouldOpenAbove ? spaceAbove : spaceBelow),
            )

            this.dropdownPlacement = shouldOpenAbove ? 'top' : 'bottom'
            this.dropdownMaxHeight = availableHeight
        },
         onSelect(type, source) {
            this.dropdownOpen = false
            this.selectedSourceId = source.id

            this.$emit('change', {
                type,
                value: source,
                name: source.name,
            })
        },
    },
}
</script>
